import type { ReactNode } from 'react'
import type { AnalyticsRangeKey, NextPageWithLayout } from '@/types'

import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ChartColumn, Clock, Download, Percent, Repeat, Table2, Wallet } from 'lucide-react'
import { downloadCsv } from '@/lib/csv'
import { TODAY } from '@/lib/date'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/dashboard/page-header'
import { SectionCard } from '@/components/dashboard/section-card'
import { SegmentedControl } from '@/components/dashboard/segmented-control'
import { KpiCard } from '@/components/dashboard/kpi-card'
import { getDelta } from '@/components/dashboard/delta-badge'
import { StackedBarChart } from '@/components/charts/stacked-bar-chart'
import { DivergingColumnChart } from '@/components/charts/diverging-column-chart'
import { FunnelChart } from '@/components/charts/funnel-chart'
import { CohortHeatmap } from '@/components/charts/cohort-heatmap'
import { BarList } from '@/components/charts/bar-list'
import { ChartLegend } from '@/components/charts/chart-tooltip'
import { getAnalytics } from '@/data/metrics'
import { PLANS } from '@/data/plans'
import { ANALYTICS_RANGE_OPTIONS } from '@/constants/dashboard'

import {
    formatNumber,
    formatPercent,
    formatRupiah,
    formatRupiahCompact,
    formatSignedNumber,
    formatSignedPercent
} from '@/lib/format'

const PLAN_SERIES = PLANS.map((plan) => ({ key: plan.id, label: plan.name, color: plan.color }))

const AnalyticsPage: NextPageWithLayout = (): ReactNode => {
    const [range, setRange] = useState<AnalyticsRangeKey>('6m')
    const [mixView, setMixView] = useState<'chart' | 'table'>('chart')
    const data = useMemo(() => getAnalytics(range), [range])
    const { nrr, ltv, cac, trialConversion, payback } = data.kpis
    const ltvChange = ltv.previous === 0 ? 0 : ((ltv.value - ltv.previous) / ltv.previous) * 100
    const ratio = cac.value === 0 ? 0 : ltv.value / cac.value
    const comparison = `vs ${data.label} sebelumnya`
    const lastMonth = data.months[data.months.length - 1]

    const exportData = (): void => {
        downloadCsv(
            `arunika-analitik-${range}-${TODAY}.csv`,
            ['Bulan', 'MRR akhir bulan (Rp)', ...PLANS.map((plan) => `MRR ${plan.name} (Rp)`), 'Pelanggan baru', 'Pelanggan berhenti', 'Trial baru'],
            data.months.map((month) => [
                month.longLabel,
                month.mrrEnd,
                ...PLANS.map((plan) => month.byPlan[plan.id]),
                month.newCustomers,
                month.churnedCustomers,
                month.trials
            ])
        )

        toast.success('Data analitik diunduh', { description: `${data.months.length} bulan terakhir dalam format CSV.` })
    }

    return (
        <>
            <Seo title='Analitik' description='Retensi, funnel konversi, dan kanal akuisisi pelanggan Arunika.' />

            <div className='space-y-6'>
                <PageHeader
                    title='Analitik'
                    description={`Pertumbuhan, retensi, dan sumber pelanggan selama ${data.label} terakhir, sampai ${lastMonth.longLabel}.`}
                    actions={(
                        <>
                            <SegmentedControl
                                label='Rentang analitik'
                                value={range}
                                onChange={setRange}
                                options={ANALYTICS_RANGE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                            />
                            <Button variant='outline' size='lg' onClick={exportData}>
                                <Download data-icon='inline-start' />
                                Unduh data
                            </Button>
                        </>
                    )}
                />

                <section aria-label='Indikator analitik' className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
                    <KpiCard
                        label='Net revenue retention'
                        icon={Repeat}
                        hint='Persentase MRR dari pelanggan lama yang bertahan setelah ekspansi, kontraksi, dan churn. Di atas 100% berarti pelanggan lama tumbuh.'
                        value={formatPercent(nrr.value)}
                        delta={{ value: `${formatSignedNumber(nrr.value - nrr.previous, 1)} poin`, ...getDelta(nrr.value - nrr.previous, false, 0.05) }}
                        comparison={comparison}
                    />

                    <KpiCard
                        label='Lifetime value'
                        icon={Wallet}
                        hint='Perkiraan laba kotor dari satu pelanggan selama berlangganan: ARPU dikali margin 80% dibagi churn bulanan.'
                        value={formatRupiahCompact(ltv.value)}
                        delta={{ value: formatSignedPercent(ltvChange), ...getDelta(ltvChange) }}
                        comparison={comparison}
                        footnote={(
                            <>
                                Rasio LTV:CAC <span className='font-semibold text-foreground'>{formatNumber(ratio, 1)}x</span>, CAC {formatRupiahCompact(cac.value)}
                            </>
                        )}
                    />

                    <KpiCard
                        label='Konversi trial'
                        icon={Percent}
                        hint='Persentase akun trial yang memilih paket berbayar.'
                        value={formatPercent(trialConversion.value)}
                        delta={{ value: `${formatSignedNumber(trialConversion.value - trialConversion.previous, 1)} poin`, ...getDelta(trialConversion.value - trialConversion.previous, false, 0.05) }}
                        comparison={comparison}
                    />

                    <KpiCard
                        label='Periode balik modal'
                        icon={Clock}
                        hint='Berapa bulan sampai biaya akuisisi satu pelanggan (CAC) tertutup oleh laba kotornya. Semakin singkat semakin baik.'
                        value={`${formatNumber(payback.value, 1)} bulan`}
                        delta={{ value: `${formatSignedNumber(payback.value - payback.previous, 1)} bulan`, ...getDelta(payback.value - payback.previous, true, 0.05) }}
                        comparison={comparison}
                    />
                </section>

                <div className='grid gap-4 xl:grid-cols-3'>
                    <SectionCard
                        className='xl:col-span-2'
                        title='MRR per paket'
                        description='Posisi MRR di akhir setiap bulan, ditumpuk menurut paket.'
                        action={(
                            <SegmentedControl
                                size='sm'
                                label='Tampilan MRR per paket'
                                value={mixView}
                                onChange={setMixView}
                                options={[
                                    { value: 'chart', label: <ChartColumn aria-hidden='true' />, ariaLabel: 'Tampilkan grafik' },
                                    { value: 'table', label: <Table2 aria-hidden='true' />, ariaLabel: 'Tampilkan tabel' }
                                ]}
                            />
                        )}
                    >
                        <ChartLegend className='mb-4' items={PLAN_SERIES.map((series) => ({ ...series }))} />

                        {mixView === 'chart'
                            ? (
                                <StackedBarChart
                                    data={data.months.map((month) => ({
                                        key: month.month,
                                        label: month.label,
                                        tooltipLabel: month.longLabel,
                                        values: month.byPlan
                                    }))}
                                    series={PLAN_SERIES}
                                    formatValue={formatRupiah}
                                    ariaLabel={`MRR per paket selama ${data.label} terakhir`}
                                    totalLabel='Total MRR'
                                />
                            )
                            : (
                                <div className='max-h-[300px] overflow-auto rounded-lg border'>
                                    <table className='w-full text-sm'>
                                        <caption className='sr-only'>MRR per paket per bulan</caption>
                                        <thead className='sticky top-0 bg-muted text-xs text-muted-foreground'>
                                            <tr>
                                                <th scope='col' className='px-3 py-2 text-left font-medium'>Bulan</th>
                                                {PLANS.map((plan) => (
                                                    <th key={plan.id} scope='col' className='hidden px-3 py-2 text-right font-medium sm:table-cell'>{plan.name}</th>
                                                ))}
                                                <th scope='col' className='px-3 py-2 text-right font-medium'>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className='divide-y'>
                                            {[...data.months].reverse().map((month) => (
                                                <tr key={month.month}>
                                                    <th scope='row' className='px-3 py-2 text-left font-normal whitespace-nowrap'>{month.longLabel}</th>
                                                    {PLANS.map((plan) => (
                                                        <td key={plan.id} className='hidden px-3 py-2 text-right whitespace-nowrap text-muted-foreground tabular-nums sm:table-cell'>
                                                            {formatRupiahCompact(month.byPlan[plan.id])}
                                                        </td>
                                                    ))}
                                                    <td className='px-3 py-2 text-right font-medium whitespace-nowrap tabular-nums'>{formatRupiahCompact(month.mrrEnd)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                    </SectionCard>

                    <SectionCard
                        title='Kanal akuisisi'
                        description={`Asal ${formatNumber(data.funnel[3].value)} pelanggan baru selama ${data.label} terakhir.`}
                    >
                        <BarList
                            items={data.channels.map((channel) => ({ key: channel.label, label: channel.label, value: channel.value }))}
                            formatValue={formatNumber}
                            showShare
                        />
                    </SectionCard>
                </div>

                <div className='grid gap-4 xl:grid-cols-3'>
                    <SectionCard
                        className='xl:col-span-2'
                        title='Pelanggan baru dan churn'
                        description='Pelanggan yang mulai berlangganan dibanding yang berhenti setiap bulan.'
                    >
                        <ChartLegend
                            className='mb-4'
                            items={[
                                { key: 'new', label: 'Pelanggan baru', color: 'var(--chart-2)' },
                                { key: 'churn', label: 'Berhenti berlangganan', color: 'var(--chart-1)' }
                            ]}
                        />
                        <DivergingColumnChart
                            data={data.months.map((month) => ({
                                key: month.month,
                                label: month.label,
                                tooltipLabel: month.longLabel,
                                positive: month.newCustomers,
                                negative: month.churnedCustomers
                            }))}
                            positiveLabel='Pelanggan baru'
                            negativeLabel='Berhenti'
                            formatValue={formatNumber}
                            ariaLabel={`Pelanggan baru dan churn per bulan selama ${data.label} terakhir`}
                        />
                    </SectionCard>

                    <SectionCard
                        title='Funnel konversi'
                        description={`Perjalanan calon pelanggan selama ${data.label} terakhir.`}
                    >
                        <FunnelChart steps={data.funnel} />
                    </SectionCard>
                </div>

                <SectionCard
                    title='Retensi kohort'
                    description='Persentase pelanggan dari tiap bulan bergabung yang masih berlangganan di bulan berikutnya.'
                >
                    <CohortHeatmap
                        rows={data.cohorts.map((cohort) => ({
                            key: cohort.month,
                            label: cohort.label,
                            size: cohort.size,
                            retention: cohort.retention
                        }))}
                    />
                </SectionCard>
            </div>
        </>
    )
}

export default AnalyticsPage
